import { t } from "i18next";
import {
  SecondaryButton,
  AccordionTable,
  Table,
} from "@sentara-health/optimahealth_web";
import { useNavigate } from "react-router";
import { UNPAID_GROUP_BALANCE } from "utils/constants/Routes";
import { UnpaidBalanceNotes } from "./UnpaidGroupBalanceTable";
import UnpaidGroupBalanceHeader from "./UnpaidGroupBalanceHeader";
import { PastDueBalanceDetailsItem } from "@sentara-health/sentara-api-hooks-core";
import { useEffect, useState } from "react";
import { convertToDate } from "utils/helpers/DateFormate";
import { amountFormat } from "utils/helpers/HelperFn";
import { DeviceTypes } from "utils/model/CommonTypes";
import { useGlobalState, useBranding, Brands } from "providers";
import { downloadXLSX } from "utils/helpers/DownloadFiles";

type Props = {
  state: Array<PastDueBalanceDetailsItem>;
  groupId: string;
  clearPastDueState: any;
  accountName: string;
};
const UnpaidGroupBalanceDetails = ({
  state,
  groupId,
  clearPastDueState,
  accountName,
}: Props) => {
  const navigate = useNavigate();
  const { deviceType } = useGlobalState();
  const { brandConfig } = useBranding();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const [groupName, setGroupName] = useState("");

  const header = [
    { text: t("unpaidGroupBalance.content.invoiceNumber") },
    { text: t("unpaidGroupBalance.content.invoiceDate") },
    { text: t("unpaidGroupBalance.content.amountBilled") },
    { text: t("unpaidGroupBalance.content.amountDue") },
    { text: t("unpaidGroupBalance.content.agentName") },
    { text: t("unpaidGroupBalance.content.daysOutstanding") },
    { text: t("unpaidGroupBalance.content.additionalInformation") },
  ];

  let total = 0;

  useEffect(() => {
    if (state?.length) {
      setGroupName(state[0].groupName);
      setGroupName(state[0].groupName);
    }
  }, [state]);

  const handleDownload = (jsonData: any) => {
    const customJson = jsonData.map((item: PastDueBalanceDetailsItem) => {
      return {
        "Invoice Number": item.invoiceNumber,
        "Invoice Date": convertToDate(item.invoiceDate),
        "Amount Billed": amountFormat(item.invoiceBalance),
        "Amount Due": amountFormat(item.amountDue),
        "Agent Name": item.agentName,
        "Days Outstanding": item.daysOverdue,
        "Additional Information": item.additionalInfo,
      };
    });
    downloadXLSX(customJson);
  };

  const getUnpaidGroupTableData = () => {
    let tableData: any = [];
    let rowData = [];

    state.map((item, key: number) => {
      rowData = [];
      total = total + item.invoiceBalance * 1;
      rowData.push({ text: item.invoiceNumber });
      rowData.push({ text: convertToDate(item.invoiceDate) });
      rowData.push({ text: amountFormat(item.amountDue) });
      rowData.push({ text: amountFormat(item.invoiceBalance) });
      rowData.push({ text: item.agentName });
      rowData.push({ text: item.daysOverdue });
      rowData.push({ text: item.additionalInfo });
      tableData.push(rowData);
    });

    return tableData;
  };

  return (
    <div>
      <UnpaidGroupBalanceHeader />
      <div className="border light-grey-bg border-radius10 p-3 mb-4">
        <UnpaidBalanceNotes />
      </div>
      <div className="d-flex flex-column gap-3 mb-4">
        <div>
          <strong data-testid="txtGroupNumberLabel">
            {t("unpaidGroupBalance.content.accountName")}:
          </strong>{" "}
          <span data-testid="txtGroupNumberValue">{accountName}</span>
        </div>
        {brandConfig.brandName !== Brands.AVMED && (
          <div>
            <strong data-testid="txtGroupNameLabel">
              {t("unpaidGroupBalance.content.groupName")}:
            </strong>{" "}
            <span data-testid="txtGroupNameValue">{groupName}</span>
          </div>
        )}
      </div>
      <div className="mb-4">
        <SecondaryButton
          data-testid="btnPerformdownloadGroupNumber"
          className={"secondary_btn btn_block_mob"}
          onClick={() => handleDownload(state)}
        >
          {t("button.label.downloadAll")}
        </SecondaryButton>
      </div>
      {!isMobile && (
        <div className="table-responsive mb-4">
          <Table
            data-testid="tblInvoiceTable"
            header={header}
            rows={getUnpaidGroupTableData()}
          />
        </div>
      )}

      {isMobile && (
        <div className="mb-4">
          <AccordionTable
            initialActiveIndex={0}
            id="unpaid_group_details_table"
            items={state.map((item, key: number) => {
              total = total + item.invoiceBalance * 1;
              return {
                title: (
                  <div>
                    <div>{t("unpaidGroupBalance.content.groupNumber")}</div>
                    <div>{item.invoiceNumber}</div>
                  </div>
                ),
                id: "unpaid_group_details_table_data",
                content: (
                  <>
                    <div className="mb-3">
                      <div>{t("unpaidGroupBalance.content.invoiceDate")}</div>
                      <div>{convertToDate(item.invoiceDate)}</div>
                    </div>
                    <div className="mb-3">
                      <div>{t("unpaidGroupBalance.content.amountBilled")}</div>
                      <div>{amountFormat(item.amountDue)}</div>
                    </div>
                    <div className="mb-3">
                      <div>{t("unpaidGroupBalance.content.amountDue")}</div>
                      <div>{amountFormat(item.invoiceBalance)}</div>
                    </div>
                    <div className="mb-3">
                      <div>
                        {t("unpaidGroupBalance.content.daysOutstanding")}
                      </div>
                      <div>{item.daysOverdue}</div>
                    </div>
                    <div className="mb-3">
                      <div>
                        {t("unpaidGroupBalance.content.additionalInformation")}
                      </div>
                      <div>{item.additionalInfo}</div>
                    </div>
                  </>
                ),
              };
            })}
          />
        </div>
      )}
      <div className="d-flex flex-column gap-3 mb-4">
        <div>
          <strong data-testid="txtUnAppliedAdjustmentsLabel">
            {t("unpaidGroupBalance.content.unappliedAdjustments")}:
          </strong>{" "}
          <span data-testid="txtUnAppliedAdjustmentsValue">None</span>
        </div>
        <div>
          <strong data-testid="txtTotalAmountDueLabel">
            {t("unpaidGroupBalance.content.totalAmountDue")}:
          </strong>{" "}
          <span data-testid="txtTotalAmountDueValue">
            {total < 0
              ? `(${amountFormat(total, true).replace("-", "")})`
              : `${amountFormat(total, true).replace("-", "")}`}
          </span>
        </div>
      </div>
      <hr className="my-4" />
      <div className="mb-4">
        <SecondaryButton
          data-testid="performNewButton"
          className={"secondary_btn btn_block_mob mb-4"}
          onClick={() => {
            clearPastDueState();
            navigate(`/app/${UNPAID_GROUP_BALANCE}`);
          }}
        >
          {t("button.label.performANewSearch")}
        </SecondaryButton>
      </div>
    </div>
  );
};

export default UnpaidGroupBalanceDetails;
